import {LoaderCircle,MapPin,Radar,ShieldCheck} from "lucide-react";
import {useInfiniteQuery} from "@tanstack/react-query";
import {useEffect,useRef,useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {api} from "../api";
import {useLocale} from "../App";
import type {NearbyPeoplePage,NearbyPerson} from "../types";
import {BottomNav,OnlineDot,PhoneShell} from "../components/Shell";

function createDirectorySeed(){return globalThis.crypto?.randomUUID?.()??`${Date.now()}-${Math.random().toString(36).slice(2)}`;}
function meterLabel(meters:number,lang:"zh"|"en"|"vn"){const value=new Intl.NumberFormat(lang==="zh"?"zh-CN":lang==="vn"?"vi-VN":"en-US").format(meters);return lang==="zh"?`距你 ${value} m`:lang==="vn"?`Cách bạn ${value} m`:`${value} m away`;}

export function LikesPage(){
  const lang=useLocale();
  const {t}=useTranslation();
  const navigate=useNavigate();
  const [seed]=useState(()=>{
    const key=`nearby-directory-seed:${lang}`;
    const existing=sessionStorage.getItem(key);
    const value=existing??createDirectorySeed();
    sessionStorage.setItem(key,value);
    return value;
  });
  const scrollRef=useRef<HTMLDivElement>(null);
  const sentinelRef=useRef<HTMLDivElement>(null);
  const query=useInfiniteQuery({
    queryKey:["nearby-people",lang,seed],
    initialPageParam:0,
    queryFn:({pageParam})=>api<NearbyPeoplePage>(`/women/directory?lang=${lang}&cursor=${pageParam}&limit=30&seed=${seed}`),
    getNextPageParam:(lastPage)=>lastPage.nextCursor??undefined,
  });
  const women=query.data?.pages.flatMap((page)=>page.women)??[];
  const total=query.data?.pages[0]?.total??0;

  useEffect(()=>{
    const root=scrollRef.current;
    const sentinel=sentinelRef.current;
    if(!root||!sentinel||!query.hasNextPage)return;
    const observer=new IntersectionObserver((entries)=>{
      if(entries[0]?.isIntersecting&&!query.isFetchingNextPage)void query.fetchNextPage();
    },{root,rootMargin:"240px 0px"});
    observer.observe(sentinel);
    return ()=>observer.disconnect();
  },[query.hasNextPage,query.isFetchingNextPage,query.fetchNextPage]);

  useEffect(()=>{
    if(!women.length||!scrollRef.current)return;
    const value=Number(sessionStorage.getItem(`nearby-directory-scroll:${lang}`)??0);
    if(value>0)scrollRef.current.scrollTop=value;
  },[lang,women.length]);
  const openProfile=(womanId:string)=>{
    sessionStorage.setItem(`nearby-directory-scroll:${lang}`,String(scrollRef.current?.scrollTop??0));
    navigate(`/${lang}/women/${womanId}`);
  };

  return <PhoneShell className="likes-page people-page">
    <header className="likes-header"><div><span><Radar/></span><div><h1>{t("peopleNearby")}</h1><p>{t("peopleSubtitle")}</p></div></div><b>{total}</b></header>
    <div className="likes-scroll" ref={scrollRef}>
      {query.isLoading?<div className="people-skeleton" aria-label={t("loadingMore")}>{Array.from({length:6},(_,index)=><i key={index}/>)}</div>:null}
      {query.isError?<div className="empty-state"><Radar/><h2>{t("nearbyLoadFailed")}</h2><button onClick={()=>void query.refetch()}>{t("tryAgain")}</button></div>:null}
      {women.length?<div className="likes-grid">{women.map((woman)=><article className="nearby-person-card" key={woman.id}>
        <button className="nearby-person-photo" type="button" aria-label={t("viewProfile")} onClick={()=>openProfile(woman.id)}>
          <img src={woman.photo_url??"/dating-v1/seed/fallback.webp"} alt={woman.name} loading="lazy" decoding="async"/>
          {woman.online?<OnlineDot/>:null}
        </button>
        <button className="nearby-person-copy" type="button" onClick={()=>openProfile(woman.id)}>
          <h2>{woman.name}, {woman.age}{woman.verified?<ShieldCheck/>:null}</h2>
          <p><MapPin/>{meterLabel(woman.distance_m,lang)}</p>
          <small>{woman.city}{woman.district?` · ${woman.district}`:""}</small>
        </button>
      </article>)}</div>:null}
      <div className="people-load-more" ref={sentinelRef} aria-live="polite">
        {query.hasNextPage?<button type="button" disabled={query.isFetchingNextPage} onClick={()=>void query.fetchNextPage()}>{query.isFetchingNextPage?<><LoaderCircle className="spin"/>{t("loadingMore")}</>:t("loadMore")}</button>:women.length?<span>{t("allPeopleLoaded")}</span>:null}
      </div>
    </div>
    <BottomNav/>
  </PhoneShell>;
}
